import { dataService } from '../apis/dataService';
import type { BalanceSheet, FinancialDataQueryParams } from '../../types/api';
import type { StackedBarData } from '../../components/charts/StackedBarChart';

/**
 * Fetch and process balance sheet data for a yearly stacked bar chart
 * Categories: Liquidity, Property Assets, Unit Value, Liabilities, Equity
 */
export async function fetchBalanceSheetStackedData(
  user_id_no: number,
  unit_id_no: number
): Promise<{ xLabels: string[], stacks: StackedBarData[] }> {
  if (!user_id_no || !unit_id_no) {
    return { xLabels: [], stacks: [] };
  }

  try {
    const params: FinancialDataQueryParams = {
      user_id_no,
      unit_id_no
    };

    const balanceSheetData = await dataService.getBalanceSheet(params);

    if (!balanceSheetData || balanceSheetData.length === 0) {
      return { xLabels: [], stacks: [] };
    }

    // Helper to extract year from date string (YYYYMM or YYYY-MM-DD)
    const extractYear = (dateStr: string): string => {
      if (dateStr.length === 6) return dateStr.substring(0, 4);
      if (dateStr.includes('-')) return dateStr.split('-')[0];
      return dateStr;
    };

    // Group data by year and category, keeping the latest date's value for each category in that year
    const yearlyCategoryData = new Map<string, Map<number, { date: string, value: number }>>();
    
    balanceSheetData.forEach(item => {
      const year = extractYear(item.calendar_date);
      if (!yearlyCategoryData.has(year)) {
        yearlyCategoryData.set(year, new Map<number, { date: string, value: number }>());
      }
      
      const yearMap = yearlyCategoryData.get(year)!;
      const existingEntry = yearMap.get(item.category_code);
      
      // Update if no entry exists yet or if this item's date is more recent
      if (!existingEntry || item.calendar_date >= existingEntry.date) {
        yearMap.set(item.category_code, {
          date: item.calendar_date,
          value: item.category_value || 0
        });
      }
    });

    // Simplify to just the values
    const yearlyData = new Map<string, Map<number, number>>();
    yearlyCategoryData.forEach((categoryMap, year) => {
      const valuesMap = new Map<number, number>();
      categoryMap.forEach((data, code) => {
        valuesMap.set(code, data.value);
      });
      yearlyData.set(year, valuesMap);
    });

    // Sort years chronologically
    const sortedYears = Array.from(yearlyData.keys()).sort((a, b) => a.localeCompare(b));

    // Prepare arrays for each stack
    const liquidityValues: number[] = [];
    const propertyAssetsValues: number[] = [];
    const unitValueValues: number[] = [];
    const liabilitiesValues: number[] = [];
    const equityValues: number[] = [];

    sortedYears.forEach(year => {
      const yearMap = yearlyData.get(year)!;
      
      // Liquidity: Cash (2) + Accounts Receivable (3)
      const liquidity = (yearMap.get(2) || 0) + (yearMap.get(3) || 0);
      
      // Property Assets: NET FF&E (4) OR sum of its children (14, 6, 7)
      // We check if code 4 has a value, otherwise we sum the individual components
      let propertyAssets = yearMap.get(4) || 0;
      if (propertyAssets === 0) {
        propertyAssets = (yearMap.get(14) || 0) + (yearMap.get(6) || 0) + (yearMap.get(7) || 0);
      }
      
      // Unit Value & Goodwill: Unit Value (8) + Goodwill (9)
      const unitValue = (yearMap.get(8) || 0) + (yearMap.get(9) || 0);
      
      // Liabilities: Unearned Revenue (11)
      const liabilities = yearMap.get(11) || 0;
      
      // Equity: Retained Earnings (12) + Equity Valuation (13)
      const equity = (yearMap.get(12) || 0) + (yearMap.get(13) || 0);

      liquidityValues.push(liquidity);
      propertyAssetsValues.push(propertyAssets);
      unitValueValues.push(unitValue);
      liabilitiesValues.push(liabilities);
      equityValues.push(equity);
    });

    return {
      xLabels: sortedYears,
      stacks: [
        { label: 'Liquidity', values: liquidityValues, color: 'info' },
        { label: 'Property Assets', values: propertyAssetsValues, color: 'primary' },
        { label: 'Unit Value', values: unitValueValues, color: 'quaternary' },
        { label: 'Liabilities', values: liabilitiesValues, color: 'error' },
        { label: 'Equity', values: equityValues, color: 'secondary' }
      ]
    };

  } catch (error) {
    console.error('Error calculating balance sheet stacked data:', error);
    return { xLabels: [], stacks: [] };
  }
}

